import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { slashArc } from './burstBuilders';
import { MOVEMENT_TRAVEL_CONFIG } from '../../../../config/abilities/movementTravel.config';
import { TRAIL_FX_CONFIG } from '../../../../config/abilities/trailFx.config';

type Primitive = VFXEffectConfig['primitives'][number];

// =============================================================================
// Trail grammar — motion-trail / afterimage verbs
// (spec: docs/plans/fx-grammar-specs/trails-and-afterimages.md §2)
// =============================================================================
//
// Sibling of burstBuilders (one-shot grammar) and statusAuraBuilders
// (persistent grammar). Three families:
//
//   projectileRibbon — a fading polyline behind any moving fed entity.
//     Emits the `ribbon` primitive: ONE graphics object per ribbon, redrawn
//     each frame from TrailSystem's position-history ring buffer — never N
//     interval one-shots (five projectiles on the old dash pattern would
//     saturate the entire 30-graphics budget, spec §4.3).
//
//   dashGhosts — N afterimage PLAYER-SPRITE snapshots at decaying alpha.
//     NOT shape grammar (primitives can't express a sprite snapshot): it
//     returns a TrailDirective consumed by the dash pipeline
//     (DashBehavior / DashTravelState → 'vfx:dash_ghost' events →
//     TrailSystem's pooled ghost sprites).
//
//   weaponSweepTrail — arc-following smear for melee swings (§2.3). Pure
//     burst grammar, zero runtime: a leading slashArc plus staggered
//     trailing sub-arcs that fade at decreasing alpha, so the smear
//     "chases" the blade. Composes into normal burst defs.
//
// Rules the verbs encode:
//  - Multi-tone by default: ribbon = core + distinct under-glow stroke;
//    sweep = edge + distinct smear tone.
//  - Ribbons carry NO tween — the history buffer owns all motion (the
//    "one owner of position" rule, spec §4.2).
//  - Sweep sub-arcs are one-shot tweens only (burst rules — bursts END);
//    the delay ladder is the promoted `delayMs` idiom (stagedBlast /
//    shadowedSlash precedent in burstBuilders — imported, not re-rolled).
// =============================================================================

/** Fading polyline behind any moving vfx entity (projectiles first). */
export function projectileRibbon(opts: {
  /** Core stroke color (school-tinted). */
  color: number;
  /** Multi-tone: distinct under-glow stroke. Default white. */
  glowColor?: number;
  /** Head width px. */
  width?: number;
  /** How much history the ribbon shows (ms). */
  lengthMs?: number;
  /** Ring-buffer cap (≈ lengthMs / frame). */
  maxPoints?: number;
  /** Width → 0 toward the tail. Default true. */
  taper?: boolean;
  /** Alpha → 0 toward the tail. Default true. */
  fade?: boolean;
  /** Route the glow stroke into the bloom layer. Default true. */
  glow?: boolean;
  /** Default 93 (under the projectile body). */
  depth?: number;
}): Primitive {
  const r = TRAIL_FX_CONFIG.ribbon;
  return {
    type: 'ribbon',
    color: opts.color,
    ribbonGlowColor: opts.glowColor ?? 0xffffff,
    width: opts.width ?? r.width,
    ribbonLengthMs: opts.lengthMs ?? r.lengthMs,
    ribbonMaxPoints: opts.maxPoints ?? r.maxPoints,
    ribbonTaper: opts.taper ?? true,
    ribbonFade: opts.fade ?? true,
    alpha: r.alpha,
    depth: opts.depth ?? r.depth,
    ...(opts.glow ?? true ? { glow: true } : {}),
    // Deliberately NO tween — TrailSystem's history owns all motion.
  };
}

/**
 * Dash-afterimage directive — consumed by the dash pipeline, NOT a shape
 * primitive (it snapshots the player sprite, spec §2.2). Plain data so it
 * can ride EventBus payloads.
 */
export interface TrailDirective {
  kind: 'ghosts';
  /** Ghost tint (school/ability color). */
  tint: number;
  /** Multi-tone second tone (edge blob) — reserved, phase-1 unused. */
  edgeTint?: number;
  /** Instant blinks: ghosts placed along the blink line. */
  count: number;
  /** Travel dashes: emit cadence (ms), clamped ≥ minGhostIntervalMs. */
  intervalMs: number;
  /** Per-ghost fade (ms). */
  lifeMs: number;
  startAlpha: number;
  /** 'add' = energy ghost. */
  blend: 'add' | 'normal';
}

/** N afterimage sprite snapshots at decaying alpha (dashes + blinks). */
export function dashGhosts(opts: {
  tint: number;
  edgeTint?: number;
  count?: number;
  intervalMs?: number;
  lifeMs?: number;
  startAlpha?: number;
  blend?: 'add' | 'normal';
}): TrailDirective {
  const g = TRAIL_FX_CONFIG.ghost;
  return {
    kind: 'ghosts',
    tint: opts.tint,
    ...(opts.edgeTint !== undefined ? { edgeTint: opts.edgeTint } : {}),
    count: opts.count ?? g.count,
    intervalMs: Math.max(
      TRAIL_FX_CONFIG.minGhostIntervalMs,
      opts.intervalMs ?? MOVEMENT_TRAVEL_CONFIG.trail.intervalMs,
    ),
    lifeMs: opts.lifeMs ?? MOVEMENT_TRAVEL_CONFIG.trail.durationMs,
    startAlpha: opts.startAlpha ?? g.startAlpha,
    blend: opts.blend ?? 'add',
  };
}

/**
 * Arc-following smear for melee swings (spec §2.3) — pure burst grammar,
 * zero runtime changes. Returns a leading slashArc (full sweep, hot alpha)
 * plus `steps` trailing sub-arcs whose angular spans progressively cover
 * EARLIER fractions of the sweep, each staggered by `stepDelayMs` (strictly
 * increasing delays) and fading at strictly decreasing alpha — the smear
 * chases the blade back toward the sweep origin.
 *
 * Shares the degrees vocabulary of slashArc/arc (arcRadius / arcStartAngle
 * / arcEndAngle, right-facing basis, ShapeSpawner mirrors on flipX).
 * Upgrades shadowedSlash consumers rather than replacing them: shadowedSlash
 * is the 2-tone static read, weaponSweepTrail the animated sweep.
 */
export function weaponSweepTrail(opts: {
  /** Shared degrees vocabulary — same fields + right-facing basis as slashArc/arc. */
  arcRadius: number;
  arcStartAngle: number;
  arcEndAngle: number;
  /** Leading-edge color. */
  color: number;
  /** Multi-tone: trailing smear tone. Default white (distinct from edge). */
  smearColor?: number;
  /** Smear sub-arcs. Default 4. */
  steps?: number;
  /** Stagger between steps (ms). Default 30. */
  stepDelayMs?: number;
  /** Per-step fade duration (ms). Default 200. */
  durationMs?: number;
  /** Leading edge stroke width. Default 6. */
  width?: number;
  glow?: boolean;
  depth?: number;
}): Primitive[] {
  const steps = opts.steps ?? 4;
  const stepDelayMs = opts.stepDelayMs ?? 30;
  const durationMs = opts.durationMs ?? 200;
  const width = opts.width ?? 6;
  const depth = opts.depth ?? 96;
  const smearColor = opts.smearColor ?? 0xffffff;
  const span = opts.arcEndAngle - opts.arcStartAngle;
  const smearWidth = Math.max(2, Math.round(width * 0.6));
  // Smear base alpha sits under the hot leading edge; each step drops it
  // further so alphas are strictly decreasing along the delay ladder.
  const smearBaseAlpha = 0.55;

  const lead = slashArc({
    radius: opts.arcRadius,
    startDeg: opts.arcStartAngle,
    endDeg: opts.arcEndAngle,
    color: opts.color,
    width,
    alpha: 0.95,
    glow: opts.glow ?? true,
    durationMs,
    depth,
  });

  const smears: Primitive[] = [];
  for (let i = 1; i <= steps; i++) {
    // Step i covers the first (steps - i + 1)/(steps + 1) of the sweep —
    // later (more delayed) steps collapse back toward the origin.
    const fraction = (steps - i + 1) / (steps + 1);
    smears.push(slashArc({
      radius: opts.arcRadius,
      startDeg: opts.arcStartAngle,
      endDeg: opts.arcStartAngle + span * fraction,
      color: smearColor,
      width: smearWidth,
      alpha: smearBaseAlpha * fraction,
      durationMs,
      fadeDelayMs: i * stepDelayMs,
      depth: depth - 1,
    }));
  }

  return [lead, ...smears];
}
